import React from 'react';
import { FlexColumn, FlexRow } from '../../../components/basic/Flex';
import { H1 } from '../../../components/basic/Text';
import { Body2, Button } from '../../../components/common';
import { useMessageGetter } from 'react-message-context';
import { ContractTerminationStatus, TerminationNotice } from './schema';
import { acceptTerminationNotice, rejectTerminationNotice } from '../../../apis_kenno';
import { formatAddress, formatDate } from '../../../utils';
import { Divider } from '../../../components/common/Divider';
import { useKennoTenantState } from '../../../context/kennoTenantContext';
import Card from '../../../components/common/Card';
import InfoBox from '../../../components/common/InfoBox';
import { NotificationType } from '../../../context/appContext';

type Props = {
  /** The termination notice data object to be displayed and processed */
  terminationNotice: TerminationNotice;
  /** Whether the current tenant is required to approve this termination notice */
  tenantApprovalRequired: boolean;
  /** Callback function to be called after the termination notice is either rejected or accepted */
  afterApproval: (url: string) => void;
};

const TerminationNoticeApproval: React.FC<Props> = ({ terminationNotice, tenantApprovalRequired, afterApproval }) => {
  const translationPrefix = 'forms.terminateLease';
  const t = useMessageGetter(translationPrefix);
  const tCommon = useMessageGetter('common');
  const contract = useKennoTenantState();

  const onAccept = async () => {
    const strongAuthUrl = await acceptTerminationNotice(terminationNotice.id);
    afterApproval(strongAuthUrl);
  };
  const onReject = async () => {
    const strongAuthUrl = await rejectTerminationNotice(terminationNotice.id);
    afterApproval(strongAuthUrl);
  };

  const renderInfoCard = () => {
    switch (terminationNotice.status) {
      case ContractTerminationStatus.APPROVED:
        return <InfoBox width="auto" margin="0" type={NotificationType.SUCCESS} message={t('noticeApproved')} />;
      case ContractTerminationStatus.READY_FOR_REVIEW:
        return <InfoBox width="auto" margin="0" type={NotificationType.SUCCESS} message={t('noticeInReview')} />;
      case ContractTerminationStatus.PENDING_TENANT_APPROVAL:
        if (tenantApprovalRequired) {
          return <InfoBox width="auto" margin="0" type={NotificationType.INFO} message={t('tenantApprovalRequired')} />;
        }
        return (
          <InfoBox width="auto" margin="0" type={NotificationType.INFO} message={t('awaitingOtherTenantApproval')} />
        );
    }
  };

  return (
    <FlexColumn padding="32px 0 0 0" gap="32px">
      <H1>{t('title')}</H1>
      {renderInfoCard()}
      <Card title={t('terminationCardTitle')}>
        {contract.data && (
          <FlexColumn>
            <span>{t('apartment')}</span>
            <Body2>{formatAddress(contract.data.postalAddress)}</Body2>
          </FlexColumn>
        )}
        <FlexColumn>
          <span>{t('endDate')}</span>
          <Body2>{formatDate(terminationNotice.endDate, '-')}</Body2>
        </FlexColumn>
        <FlexColumn>
          <span>{t('terminationReason')}</span>
          <Body2>{t(terminationNotice.terminationReason)}</Body2>
        </FlexColumn>
        <FlexColumn>
          <span>{t('masterKeyAccessAllowed')}</span>
          <Body2>{terminationNotice.masterKeyAccessAllowed ? tCommon('yes') : tCommon('no')}</Body2>
        </FlexColumn>
        <FlexColumn>
          <span>{t('hasPetsInApartment')}</span>
          <Body2>{terminationNotice.hasPetsInApartment ? tCommon('yes') : tCommon('no')}</Body2>
        </FlexColumn>
        <FlexColumn>
          <span>{t('additionalDetails')}</span>
          <Body2>{terminationNotice.additionalDetails || '-'}</Body2>
        </FlexColumn>
      </Card>
      <Card title={t('display')}>
        <FlexColumn>
          <span>{t('givingContactInfoAllowed')}</span>
          <Body2>{terminationNotice.displaysSelf ? tCommon('yes') : tCommon('no')}</Body2>
        </FlexColumn>
      </Card>

      <Card title={t('newAddress.title')}>
        <FlexColumn>
          <Body2>
            {terminationNotice.newAddress.recipientName}
            <br />
            {terminationNotice.newAddress.streetAddress},&nbsp;{terminationNotice.newAddress.postalCode}
            <br />
            {terminationNotice.newAddress.city}
          </Body2>
        </FlexColumn>
      </Card>
      <Card title={t('beneficiary')}>
        {terminationNotice.beneficiaries.map((beneficiary) => {
          return (
            <FlexColumn key={beneficiary.beneficiaryName}>
              <span>{t('beneficiaries.beneficiaryName')}</span>
              <Body2>{beneficiary.beneficiaryName}</Body2>
              <span>{t('beneficiaries.iban')}</span>
              <Body2>
                {beneficiary.bic} {beneficiary.iban}
              </Body2>
            </FlexColumn>
          );
        })}
      </Card>
      {tenantApprovalRequired && (
        <>
          <Divider />
          <FlexRow padding="32px 0" gap="32px" justifyContent="center" wrap="wrap">
            <Button icon="send" onClick={onAccept} primary>
              {t('acceptNotice')}
            </Button>
            <Button icon="xClose" onClick={onReject}>
              {t('rejectNotice')}
            </Button>
          </FlexRow>
        </>
      )}
    </FlexColumn>
  );
};

export default TerminationNoticeApproval;
